import React, { FC, memo, useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useI18n, useLogger } from '~Hooks';
import Icon from '~ComponentsImproved/Icon';
import useOffers from '~Hooks/useOffers';
import percentIcon from '../../../../../../Assets/icons/percent_discount.svg';
import { useExpressCart, useExpressCartActions } from '../../../../Providers/ExpressCartProvider';
import { useExpressProductLibrary } from '../../../../Providers/ExpressProductLibraryProvider';
import CodeInput from '../../../../Components/CodeInput';
import Modal, { MODAL_TRANSITION_MS } from '../../../../Components/Modal';

/*
 * Entrance plays as a plain keyframe animation, unconditionally, the instant
 * the node mounts - relying on CSSTransition's enter/enter-active class swap
 * instead raced with React's batching and could skip straight to the end
 * state. Exit stays CSSTransition-driven: the exit-active/exit-done classes
 * turn the animation off and hand over to `transition` for the way out.
 */
/** The card: scales and fades in, and back out. */
const Card = styled.div`
  position: relative;
  width: min(100%, 860px);
  padding: 48px;
  border-radius: 36px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  background: var(--eposSurface);
  color: var(--eposText);
  box-shadow: 0 40px 100px rgba(0, 0, 0, 0.45);
  transform: translate3d(0, 0, 0) scale(1);
  opacity: 1;
  animation: eposPromoCardIn ${MODAL_TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1) both;
  transition: transform ${MODAL_TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity ${MODAL_TRANSITION_MS}ms ease-out;
  will-change: transform, opacity;

  .epos-promo-exit-active &,
  .epos-promo-exit-done & {
    animation: none;
    transform: translate3d(0, 24px, 0) scale(0.96);
    opacity: 0;
  }

  @keyframes eposPromoCardIn {
    from {
      transform: translate3d(0, 24px, 0) scale(0.96);
      opacity: 0;
    }
    to {
      transform: translate3d(0, 0, 0) scale(1);
      opacity: 1;
    }
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 32px;
  font-weight: 800;
  letter-spacing: -0.6px;
`;

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Enter a code; a valid one is applied on the spot and the card closes - the
 * cart row then shows what it gave. Applying goes through
 * `setAppliedDiscount` on the cart - the same shape POS uses - so pricing and
 * the order payload need nothing new.
 */
const PromoCodeModal: FC<Props> = ({ open, onClose }) => {
  const { i18n } = useI18n();
  const logger = useLogger('epos-promo-code');
  const offers = useOffers();
  const expressCart = useExpressCart();
  const cartActions = useExpressCartActions();
  const productLibrary = useExpressProductLibrary();

  const [error, setError] = useState('');

  /** Closed from anywhere (incl. the idle timeout) - the next open starts without an error. */
  useEffect(() => {
    if (!open) {
      setError('');
    }
  }, [open]);

  /** Typing again hides the error - a no-op render-wise when there is none. */
  const clearError = useCallback(() => setError(''), []);

  /** Valid code -> applied straight away, no confirmation step. */
  const handleSubmit = useCallback(
    (code: string) => {
      const result = offers.findByPromoCode(code, expressCart.serviceType);
      if (!result.success || !result.promoCode) {
        logger.info('Promo code rejected', { code, reason: result.errorMessage });
        setError(result.errorMessage ?? i18n('PromoCode.Error.NotFound'));
        return;
      }

      const promoCode = result.promoCode;
      // Items the code is limited to, via the library's category map. Empty = whole order.
      const itemIds = (promoCode.categoryIds ?? []).flatMap(
        (categoryId) => productLibrary.categoryItemIds[categoryId] ?? [],
      );

      logger.info('Applying promo code', { promoCodeId: promoCode.id, itemCount: itemIds.length });
      cartActions.setAppliedDiscount({
        id: promoCode.id,
        type: promoCode.discountType,
        value: promoCode.discountRate,
        itemIds,
        title: i18n(promoCode.title),
      });
      onClose();
    },
    [
      offers.findByPromoCode,
      expressCart.serviceType,
      productLibrary.categoryItemIds,
      cartActions,
      onClose,
    ],
  );

  return (
    <Modal open={open} exitPrefix="epos-promo" onBackdropTap={onClose} testId="epos-promo-modal">
      <Card>
        <Header>
          <Icon src={percentIcon} color="var(--eposText)" width="36px" height="36px" />
          <Title>{i18n('Express.PromoCode.Have')}</Title>
        </Header>
        <CodeInput
          placeholder={i18n('PromoCode.EnterCode')}
          submitLabel={i18n('PromoCode.Apply')}
          onSubmit={handleSubmit}
          error={error}
          onChange={clearError}
        />
      </Card>
    </Modal>
  );
};

export default memo(PromoCodeModal);
